import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  PROJECT_PRIORITY_COLORS,
  displayName,
} from '../../pm/constants';
import { formatDueDate, formatMonthDay } from '../../pm/dueDate';
import { subscribeMyNotifications, updateTask } from '../../pm/firebase';
import { linkify } from '../../pm/linkify';
import {
  AppUser,
  Notification,
  Project,
  Task,
  TaskStatus,
  Workstream,
} from '../../pm/types';
import StatusPicker from './StatusPicker';

interface Props {
  uid: string;
  tasks: Task[];
  projects: Project[];
  workstreams: Workstream[];
  admins: AppUser[];
}

// Compact relative-time helper. Used for the "Updated X by <Name>" line.
// Aligns with what people glance at: hours during the day, then days.
function relativeTime(ms: number, now: number): string {
  const diff = now - ms;
  const min = 60_000;
  const hour = 60 * min;
  const day = 24 * hour;
  if (diff < min) return 'just now';
  if (diff < hour) return `${Math.floor(diff / min)}m ago`;
  if (diff < day)  return `${Math.floor(diff / hour)}h ago`;
  if (diff < 2 * day) return 'yesterday';
  return `${Math.floor(diff / day)}d ago`;
}

// Calendar bounds for "today" and "this week". Week boundary uses local time
// and closes on the following Sunday at 23:59:59.999. Computed once per
// render — re-renders triggered by the data subscriptions are fast enough
// that we don't need a 1-min ticker like DueDatePicker does.
function todayBounds(now: number): { dayStart: number; dayEnd: number; weekEnd: number } {
  const d = new Date(now);
  const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayEnd = dayStart + 86400000 - 1;
  // 0 = Sunday, 6 = Saturday in JS. Push forward to the *next* Sunday's end.
  const dow = d.getDay();
  const daysToSunday = (7 - dow) % 7; // 0 if today is Sunday
  const weekEndStart = new Date(d.getFullYear(), d.getMonth(), d.getDate() + daysToSunday).getTime();
  const weekEnd = weekEndStart + 86400000 - 1;
  return { dayStart, dayEnd, weekEnd };
}

// Sort active tasks earliest-due first; tasks without a due date sort last
// within their section (they only live in "No due date" anyway).
function compareByDue(a: Task, b: Task): number {
  const ad = typeof a.dueDate === 'number' ? a.dueDate : Number.POSITIVE_INFINITY;
  const bd = typeof b.dueDate === 'number' ? b.dueDate : Number.POSITIVE_INFINITY;
  if (ad !== bd) return ad - bd;
  return a.label.localeCompare(b.label);
}

interface Section {
  key: string;
  label: string;
  emphasis?: 'overdue' | 'today' | 'soon' | 'blocked' | 'mention';
  tasks: Task[];
}

const MyWorkView: React.FC<Props> = ({ uid, tasks, projects, workstreams, admins }) => {
  const history = useHistory();
  const nowMs = Date.now();
  const { dayStart, dayEnd, weekEnd } = todayBounds(nowMs);

  // Index for fast lookups by id when rendering a row.
  const projectById = useMemo(() => {
    const m = new Map<string, Project>();
    for (const p of projects) m.set(p.id, p);
    return m;
  }, [projects]);
  const workstreamById = useMemo(() => {
    const m = new Map<string, Workstream>();
    for (const w of workstreams) m.set(w.id, w);
    return m;
  }, [workstreams]);

  // Subscribe to my @mention notifications so the "Mentioned" section can
  // pull in tasks I don't own but where someone tagged me.
  const [mentions, setMentions] = useState<Notification[]>([]);
  useEffect(() => {
    if (!uid) {
      setMentions([]);
      return;
    }
    const unsub = subscribeMyNotifications(uid, (items) => {
      setMentions(items.filter((n) => n.type === 'mention'));
    });
    return unsub;
  }, [uid]);

  // Set of task ids where I have at least one mention notification. Used for
  // the Mentioned section; we exclude ones I already own to avoid duplicates.
  const mentionedTaskIds = useMemo(() => {
    const s = new Set<string>();
    for (const n of mentions) s.add(n.taskId);
    return s;
  }, [mentions]);

  // Categorize my tasks. Each task lands in exactly one section above the
  // Mentioned subsection (which is a parallel surface for tasks I don't own).
  const sections = useMemo<Section[]>(() => {
    if (!uid) return [];

    const mine = tasks.filter((t) => t.ownerId === uid);
    const active = mine.filter((t) => t.status === 'todo' || t.status === 'progress');
    const blocked = mine.filter((t) => t.status === 'blocked');

    const overdue: Task[] = [];
    const dueToday: Task[] = [];
    const dueThisWeek: Task[] = [];
    const later: Task[] = [];
    const noDue: Task[] = [];

    for (const t of active) {
      if (typeof t.dueDate !== 'number') {
        noDue.push(t);
        continue;
      }
      if (t.dueDate < dayStart) overdue.push(t);
      else if (t.dueDate <= dayEnd) dueToday.push(t);
      else if (t.dueDate <= weekEnd) dueThisWeek.push(t);
      else later.push(t);
    }

    overdue.sort(compareByDue);
    dueToday.sort(compareByDue);
    dueThisWeek.sort(compareByDue);
    later.sort(compareByDue);
    noDue.sort((a, b) => a.label.localeCompare(b.label));
    blocked.sort(compareByDue);

    // Mentioned: tasks I'm tagged in via a notification but don't own. Drop
    // any that no longer exist or are already done — a stale notification
    // shouldn't keep cluttering the queue.
    const mentioned = tasks.filter(
      (t) => mentionedTaskIds.has(t.id) && t.ownerId !== uid && t.status !== 'done',
    );
    mentioned.sort(compareByDue);

    const out: Section[] = [];
    if (overdue.length)      out.push({ key: 'overdue',   label: 'Overdue',       emphasis: 'overdue', tasks: overdue });
    if (dueToday.length)     out.push({ key: 'today',     label: 'Due today',     emphasis: 'today',   tasks: dueToday });
    if (dueThisWeek.length)  out.push({ key: 'week',      label: 'Due this week', emphasis: 'soon',    tasks: dueThisWeek });
    if (later.length)        out.push({ key: 'later',     label: 'Later',                              tasks: later });
    if (blocked.length)      out.push({ key: 'blocked',   label: 'Blocked',       emphasis: 'blocked', tasks: blocked });
    if (noDue.length)        out.push({ key: 'undated',   label: 'No due date',                        tasks: noDue });
    if (mentioned.length)    out.push({ key: 'mentioned', label: 'Mentioned',     emphasis: 'mention', tasks: mentioned });
    return out;
  }, [tasks, uid, dayStart, dayEnd, weekEnd, mentionedTaskIds]);

  const totalActive = useMemo(() => {
    return sections
      .filter((s) => s.key !== 'mentioned')
      .reduce((acc, s) => acc + s.tasks.length, 0);
  }, [sections]);

  async function onStatusChange(task: Task, next: TaskStatus) {
    if (!uid) return;
    try {
      await updateTask(task.id, { status: next }, uid);
    } catch {
      // Errors propagate via the realtime subscription; nothing to do here.
    }
  }

  function openTask(task: Task) {
    history.push({
      pathname: `/Projects/${task.projectId}`,
      hash: `#task-${task.id}`,
    });
  }

  if (!uid) {
    return (
      <div className="pm-empty">
        <h3>Sign in to see your work</h3>
        <p>My work pulls tasks assigned to you and mentions you've received.</p>
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <div className="pm-mywork-empty">
        <h3>All caught up</h3>
        <p>No assigned tasks, no overdue items, no mentions. Nice.</p>
      </div>
    );
  }

  return (
    <div className="pm-mywork">
      <div className="pm-mywork-summary">
        {totalActive === 0
          ? 'No active work assigned — only mentions below.'
          : `${totalActive} active ${totalActive === 1 ? 'item' : 'items'} assigned to you`}
      </div>

      {sections.map((section) => (
        <section key={section.key} className={`pm-mywork-section pm-mywork-section-${section.key}`}>
          <header className="pm-mywork-section-header">
            <span
              className={`pm-mywork-section-dot${section.emphasis ? ` pm-mywork-section-dot-${section.emphasis}` : ''}`}
            />
            <h3>{section.label}</h3>
            <span className="pm-mywork-section-count">{section.tasks.length}</span>
          </header>

          <ul className="pm-mywork-list">
            {section.tasks.map((task) => {
              const project = projectById.get(task.projectId);
              const ws = workstreamById.get(task.workstreamId);
              const due = typeof task.dueDate === 'number'
                ? formatDueDate(task.dueDate, nowMs, task.status === 'done')
                : null;
              const updater = task.updatedBy
                ? admins.find((a) => a.uid === task.updatedBy)
                : null;
              const updaterName = updater ? displayName(updater.email) : null;
              const priColor = project?.priority
                ? PROJECT_PRIORITY_COLORS[project.priority]
                : 'var(--rr-stone-400)';
              return (
                <li key={task.id} className="pm-mywork-row">
                  {/* Status chip on the LEFT — primary action and at-a-glance
                      signal, mirroring the design's redesign of My Work. */}
                  <div className="pm-mywork-row-status">
                    {section.key === 'mentioned' ? (
                      <span className="pm-mywork-mention-tag" title="You were mentioned on this task">
                        @mention
                      </span>
                    ) : (
                      <StatusPicker
                        status={task.status}
                        onChange={(s) => onStatusChange(task, s)}
                      />
                    )}
                  </div>
                  <div className="pm-mywork-row-main">
                    <button
                      type="button"
                      className="pm-mywork-row-title pm-link-button"
                      onClick={() => openTask(task)}
                      title={task.label}
                    >
                      {linkify(task.label, { stopPropagation: true })}
                    </button>
                    <div className="pm-mywork-row-meta">
                      {ws ? <span className="pm-mywork-ws-name">{ws.title}</span> : null}
                      {updaterName && task.updatedAt ? (
                        <>
                          <span className="pm-mywork-sep" aria-hidden>·</span>
                          <span className="pm-mywork-updated">
                            Updated {relativeTime(task.updatedAt, nowMs)} by <b>{updaterName}</b>
                          </span>
                        </>
                      ) : null}
                      {task.description ? (
                        <span className="pm-mywork-row-desc">
                          {linkify(task.description, { stopPropagation: true })}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="pm-mywork-row-side">
                    {due ? (
                      <span
                        className={`pm-pill pm-due pm-due-${due.tone}`}
                        title={formatMonthDay(task.dueDate as number, { withYear: true })}
                      >
                        {due.label}
                      </span>
                    ) : (
                      <span className="pm-mywork-due-none">No date</span>
                    )}
                    {project ? (
                      <button
                        type="button"
                        className="pm-mywork-project-chip"
                        onClick={() => history.push(`/Projects/${project.id}`)}
                        title={`Open ${project.title}`}
                      >
                        <span className="pm-pri-dot" style={{ background: priColor, width: 6, height: 6 }} />
                        {project.title}
                      </button>
                    ) : (
                      <span className="pm-mywork-project-chip pm-mywork-project-chip-missing">
                        Unknown project
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
};

export default MyWorkView;
